import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon, EditIcon, RotateCwIcon } from '@persona-id/design-tokens/icons';
import { compact, omit, startCase, uniqBy } from 'lodash/fp';
import { ChangeEvent, useCallback, useMemo, useState, VFC } from 'react';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import ConditionalConfiguration from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/ConditionalConfiguration';
import EditConditionalPanel from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/EditConditionalPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import { EMPTY_LOGIC_BLOCK_OR_V2, UsableObjectConfig } from 'dashboard/lib/workflows';
import {
  getPlatformObjectTypeFromJsonSchema,
  isPlatformObjectSchema,
} from 'dashboard/lib/workflows/platform-object';
import { WorkflowError, WorkflowFieldObjectKey, WorkflowSchema } from 'dashboard/models/workflow';
import {
  INPUT_STEP_TOKEN,
  StaticWorkflowStepActionKey,
  WORKFLOW_ACTION_TO_OBJECT,
  WORKFLOW_STEP_NEW_ACTION_NAME,
  WORKFLOW_WAITABLE_EVENT_FOR_ACTION,
  WORKFLOW_WAITABLE_EVENT_FOR_FETCH_ACTION,
  WORKFLOW_WAITABLE_EVENT_FOR_OBJECT,
  WORKFLOW_WAITABLE_EVENT_FOR_TRIGGER,
  WORKFLOW_WAITABLE_REPEATABLE_EVENT,
  WorkflowActionFetchPersonaObjectV1,
  WorkflowConditional,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepInput,
  WorkflowStepType,
  WorkflowStepWait,
  WorkflowStepWaitEvent,
} from 'dashboard/models/workflow-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { EVENT_NAME_TO_DESCRIPTION, EventName } from 'lib/constants/events';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchema } from 'lib/json-schema';
import { buildGraph, findPath, generateWorkflowToken } from 'lib/workflows';

const StyledTooltip = styled(Tooltip)`
  margin-left: ${Spacing[1]};
  vertical-align: middle;
`;

const StyledParagraph = styled(Paragraph)`
  width: 250px;
`;
const MIN_WAIT_TIME_SECONDS = 60;

enum WaitableType {
  Object = 'object',
  Time = 'time',
}

interface StepConfigWaitProps {
  step: WorkflowStepWait;
  stepsConfig: WorkflowStep[];
  editable: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  usableObjects: UsableObjectConfig[];
  tokensToDisplayValue: Record<string, string>;
  workflowSchema: WorkflowSchema;
  inputStep: WorkflowStepInput;
  hasWorkflowsEnhanced: boolean;
  updateStep: (newStep: WorkflowStepWait) => void;
  focusStep: (token: string) => void;
}

const stepNameFor = (
  watchedEvents: WorkflowStepWait['watched-events'],
  stepName: string,
): string => {
  if (watchedEvents == null) {
    return stepName;
  }

  const eventSize = watchedEvents.length;

  if (eventSize === 0 || (eventSize === 1 && !watchedEvents[0]['event-name'])) {
    return WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Wait];
  }
  if (eventSize === 1 && watchedEvents[0]['event-name']) {
    return `Wait for ${EVENT_NAME_TO_DESCRIPTION[watchedEvents[0]['event-name']]}`;
  }
  return `Wait for any of ${eventSize} events`;
};

const isWaitableActionKey = (
  actionKey: StaticWorkflowStepActionKey,
): actionKey is WorkflowStepActionKey => {
  return Object.keys(WORKFLOW_WAITABLE_EVENT_FOR_ACTION).includes(actionKey);
};

const isWaitableObjectKey = (objectKey: WorkflowFieldObjectKey) => {
  return Object.keys(WORKFLOW_WAITABLE_EVENT_FOR_OBJECT).includes(objectKey);
};

export const generateSelectableEvents = ({
  inputStep,
  waitStep,
  stepsConfig,
}: {
  inputStep: WorkflowStepInput;
  waitStep: WorkflowStepWait;
  stepsConfig: WorkflowStep[];
}) => {
  const { trigger, inputs } = inputStep;
  const triggerEventWaits = (
    trigger?.type === ApiModel.TriggerEvent
      ? (WORKFLOW_WAITABLE_EVENT_FOR_TRIGGER[trigger.attributes['event-name']] ?? [])
      : []
  ).map((eventName) => {
    return { objectName: 'Workflow Trigger', stepToken: 'trigger', event: eventName };
  });

  const inputWaits = inputs.flatMap(({ schema, token, name }) => {
    const parsedSchema = JSON.parse(schema);
    if (!isPlatformObjectSchema(parsedSchema)) {
      return [];
    }
    const objectKey = getPlatformObjectTypeFromJsonSchema(parsedSchema);
    if (!objectKey || !isWaitableObjectKey(objectKey)) {
      return [];
    }

    return (WORKFLOW_WAITABLE_EVENT_FOR_OBJECT[objectKey] || []).map((eventName) => {
      return {
        objectName: `Input ${name} (${startCase(objectKey)})`,
        stepToken: `${INPUT_STEP_TOKEN}.${token}`,
        event: eventName,
      };
    });
  });

  const pathToWaitStep = findPath(waitStep.token, stepsConfig);
  return pathToWaitStep
    .filter((step): step is WorkflowStepAction => {
      if (step.type !== WorkflowStepType.Action || step.action == null) {
        return false;
      }

      const actionKey = step.action.key as StaticWorkflowStepActionKey;
      const objectKey = WORKFLOW_ACTION_TO_OBJECT[actionKey];
      return (
        actionKey === WorkflowStepActionKey.FetchPersonaObject ||
        isWaitableActionKey(actionKey) ||
        (!!objectKey && isWaitableObjectKey(objectKey))
      );
    })
    .map(({ token, action, name }) => ({
      actionKey: action.key,
      stepName: name,
      params: action.params,
      token,
    }))
    .flatMap((actionAttributes) => {
      let eventItems: WorkflowStepWaitEvent[] = [];
      const actionKey = actionAttributes.actionKey as StaticWorkflowStepActionKey;
      const objectKey = WORKFLOW_ACTION_TO_OBJECT[actionKey];
      if (actionKey === WorkflowStepActionKey.FetchPersonaObject) {
        const fetchParams = actionAttributes.params as WorkflowActionFetchPersonaObjectV1['params'];
        eventItems = WORKFLOW_WAITABLE_EVENT_FOR_FETCH_ACTION[fetchParams['object-type']] || [];
      } else if (isWaitableActionKey(actionKey)) {
        eventItems = WORKFLOW_WAITABLE_EVENT_FOR_ACTION[actionKey] || [];
      } else if (objectKey && isWaitableObjectKey(objectKey)) {
        eventItems = WORKFLOW_WAITABLE_EVENT_FOR_OBJECT[objectKey] || [];
      }
      return eventItems.map((eventName) => ({
        ...actionAttributes,
        eventName,
      }));
    })
    .map(({ eventName, stepName, token }) => {
      return { objectName: `Object from ${stepName} step`, stepToken: token, event: eventName };
    })
    .concat([...triggerEventWaits, ...inputWaits]);
};

// 3 days to seconds
export const DEFAULT_MAX_WAIT_TIME_NON_ENHANCED_SECONDS = 3 * 24 * 60 * 60;
// 30 days to seconds
export const DEFAULT_MAX_WAIT_TIME_SECONDS = 30 * 24 * 60 * 60;

const DEFAULT_DELAY_PARAMS: Pick<
  WorkflowStepWait,
  'wait-period-seconds' | 'watched-events' | 'name'
> = {
  // 0 is ok as default because realistically no one will need a 0 second delay
  'wait-period-seconds': 0,
  'watched-events': null,
  name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Wait],
};
export const DEFAULT_WAIT_PARAMS: Pick<WorkflowStepWait, 'wait-period-seconds' | 'watched-events'> =
  {
    // enhanced/non-enhanced specific wait times only apply to delay wait steps, not event wait steps.
    // use the highest max as a fallback. this should rarely trigger as the event will most likely fire first.
    'wait-period-seconds': DEFAULT_MAX_WAIT_TIME_SECONDS,
    'watched-events': [],
  };

const StepConfigWait: VFC<StepConfigWaitProps> = ({
  step,
  stepsConfig,
  editable,
  errors,
  jsonSchema,
  tokensToDisplayValue,
  usableObjects,
  workflowSchema,
  updateStep,
  focusStep,
  inputStep,
  hasWorkflowsEnhanced,
}) => {
  const defaultMaxWaitTimeSeconds = !hasWorkflowsEnhanced
    ? DEFAULT_MAX_WAIT_TIME_NON_ENHANCED_SECONDS
    : DEFAULT_MAX_WAIT_TIME_SECONDS;

  const tokensToSteps = buildGraph(stepsConfig);
  const selectables = useMemo(
    () => generateSelectableEvents({ inputStep, waitStep: step, stepsConfig }),
    [inputStep, step, stepsConfig],
  );

  const initialWaitableType =
    step['wait-period-seconds'] != null && step['watched-events'] == null
      ? WaitableType.Time
      : WaitableType.Object;
  const [waitableType, setWaitableType] = useState(initialWaitableType);

  const selectWaitable = (waitableType: WaitableType) => {
    setWaitableType(waitableType);
    // Set/reset defaults
    if (waitableType === WaitableType.Object) {
      updateStep({
        ...step,
        ...DEFAULT_WAIT_PARAMS,
      });
    } else {
      updateStep({
        ...omit(['conditional'], step),
        ...DEFAULT_DELAY_PARAMS,
      });
    }
  };

  const [selectedObjectToken, selectObjectToken] = useState<string | undefined>(
    step['watched-events']?.[0]?.['event-object-step-token'],
  );

  const showConditional = useMemo(
    () =>
      (step['watched-events'] && step['watched-events']?.length > 1) ||
      (step.conditional && step.conditional.rules.or.length > 0) ||
      (step['watched-events']?.length === 1 &&
        step['watched-events'][0]['event-name'] &&
        WORKFLOW_WAITABLE_REPEATABLE_EVENT.includes(step['watched-events'][0]['event-name'])),
    [step],
  );

  const [openConditionalEditModal, setOpenConditionalEditModal] = useState(false);

  const updateConditional = useCallback(
    (newConditional: WorkflowConditional) => {
      updateStep({
        ...step,
        conditional: newConditional.rules.or.length > 0 ? newConditional : undefined,
      });

      if (newConditional.rules.or.length === 0) {
        setOpenConditionalEditModal(false);
      }
    },
    [step, updateStep],
  );

  const waitableObjects = useMemo(
    () =>
      uniqBy(({ stepToken }) => stepToken, selectables).map((selectable) => ({
        label: selectable.objectName,
        value: selectable.stepToken,
      })),
    [selectables],
  );

  // selected object, may need to reset event selections
  const selectObject = (selectedStepToken: string) => {
    if (selectedStepToken !== selectedObjectToken) {
      selectObjectToken(selectedStepToken);
      selectEvents([]);
      // Track interim select state so we can show validation errors if user doesn't complete selection
      updateStep({
        ...step,
        'watched-events': [
          { 'event-name': undefined, 'event-object-step-token': selectedStepToken },
        ],
      });
    }
  };

  const waitableEvents = useMemo(
    () =>
      selectables
        .filter((selectable) => selectable.stepToken === selectedObjectToken)
        .map((selectable) => ({
          label: (
            <>
              {EVENT_NAME_TO_DESCRIPTION[selectable.event]}
              {WORKFLOW_WAITABLE_REPEATABLE_EVENT.includes(selectable.event) && (
                <StyledTooltip
                  iconTrigger={<RotateCwIcon size={14} />}
                  zIndex={Z_INDICES.toast}
                  hideArrow
                  aria-label='repeatable event'
                >
                  <StyledParagraph size='sm'>
                    This event is repeatable. Only instances that happen after this step begins
                    waiting will cause the run to continue.
                  </StyledParagraph>
                </StyledTooltip>
              )}
            </>
          ),
          value: selectable.event,
        })),
    [selectables, selectedObjectToken],
  );

  const selectedEvents = useMemo(
    () => compact(step['watched-events']?.map((event) => event['event-name'])),
    [step],
  );

  const selectEvents = (newSelectedEvents: EventName[]) => {
    if (selectedObjectToken) {
      const newWatchedEvents = newSelectedEvents.map((selectedEventName) => ({
        'event-name': selectedEventName,
        'event-object-step-token': selectedObjectToken,
      }));

      // when previous step.name doesn't match to default name, keep it as is
      const newName =
        stepNameFor(step['watched-events'], step.name) === step.name
          ? stepNameFor(newWatchedEvents, step.name)
          : step.name;

      updateStep({
        ...step,
        name: newName,
        'watched-events': newWatchedEvents,
      });
    }
  };

  const eventsLabelTextMapper = useCallback((values: EventName[]) => {
    if (values.length === 0) {
      return 'Select...';
    } else if (values.length === 1) {
      return EVENT_NAME_TO_DESCRIPTION[values[0]];
    } else {
      return `Any of ${values.length} selected events`;
    }
  }, []);

  const onChangeWaitPeriodSeconds = (event: ChangeEvent<HTMLInputElement>) => {
    updateStep({
      ...step,
      'wait-period-seconds': event.target.value ? parseInt(event.target.value) : undefined,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<
    'watched-events' | 'wait-period-seconds' | 'conditional'
  >(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Wait for' htmlFor='step-config-wait__type' />
      <Select
        disabled={!editable}
        id='step-config-wait__type'
        value={waitableType}
        onValueChange={selectWaitable}
      >
        <Option value={WaitableType.Object}>Object</Option>
        <Option value={WaitableType.Time}>Time</Option>
      </Select>
      {waitableType === WaitableType.Object && (
        <>
          <Spacer height={2} />
          <ParamLabel htmlFor='step-config-wait__object' labelText='Target Object' />
          <Select
            disabled={!editable || waitableObjects.length === 0}
            hasError={selectedObjectToken == null && hasErrorFor('watched-events')}
            id='step-config-wait__object'
            placeholder={
              waitableObjects.length === 0
                ? 'No target objects available'
                : 'Select target object(s)'
            }
            value={selectedObjectToken}
            onValueChange={selectObject}
          >
            {waitableObjects.map(({ value, label }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
          {selectedObjectToken == null && renderErrorMessageFor('watched-events')}
          {selectedObjectToken != null && (
            <>
              <Spacer height={2} />
              <ParamLabel
                helpText='This step resolves once any of the selected events happen. Use a parallel step if
            you prefer to wait for all of the events to happen before proceeding further in
            the Workflow.'
                htmlFor='step-config-wait__events__placeholder'
                labelText='Events'
              />
              <MultiSelect
                data-test='step-config-wait__events'
                disabled={!editable || waitableObjects.length === 0}
                hasError={hasErrorFor('watched-events')}
                labelTextMapper={eventsLabelTextMapper}
                selectOptionValues={waitableEvents}
                size='md'
                value={selectedEvents}
                onChange={selectEvents}
                onCloseOptions={() => {
                  focusStep(step.token);
                }}
                onMouseOverOption={() => {
                  focusStep(selectedObjectToken);
                }}
              />
              {renderErrorMessageFor('watched-events')}
              <Spacer height={2} />
              {showConditional && (
                <>
                  <ParamLabel
                    helpText='This condition is evaluated only after one of the selected events happen.
                          The step will resolve once one of the selected events happen and the condition evaluates to true.'
                    labelText='Additional Condition'
                    buttons={[
                      <Button
                        key='edit'
                        width='icon'
                        variant='basic'
                        disabled={!editable}
                        onClick={() => setOpenConditionalEditModal(true)}
                        id='step-config-wait__edit-conditional-button'
                        aria-label='Edit additional condition'
                      >
                        <EditIcon />
                      </Button>,
                    ]}
                  />
                  {!step.conditional || step.conditional.rules.or.length === 0 ? (
                    <Paragraph>
                      No condition specified. Wait step will resolve once any of the selected events
                      happens.
                    </Paragraph>
                  ) : (
                    <ConditionalConfiguration
                      headingText=''
                      editable={false}
                      conditional={step.conditional}
                      errors={errors}
                      jsonSchema={jsonSchema}
                      tokensToDisplayValue={tokensToDisplayValue}
                      tokensToSteps={tokensToSteps}
                      usableObjects={usableObjects}
                      onEditClick={() => setOpenConditionalEditModal(true)}
                    />
                  )}
                  <Spacer height={2} />
                  {editable && openConditionalEditModal && (
                    <EditConditionalPanel
                      panelTitle='Edit additional wait condition'
                      conditional={
                        step.conditional || {
                          token: generateWorkflowToken(),
                          name: '',
                          rules: EMPTY_LOGIC_BLOCK_OR_V2,
                        }
                      }
                      workflowSchema={workflowSchema}
                      jsonSchema={jsonSchema}
                      tokensToDisplayValue={tokensToDisplayValue}
                      allowEditName={false}
                      updateConditional={updateConditional}
                      onClose={() => {
                        setOpenConditionalEditModal(false);
                      }}
                    />
                  )}
                </>
              )}
              <ParamLabel
                helpText={`Default resolution time ensures your wait step will timeout after a specified period of seconds.
                  If that time period gets reached before the wait event resolves, your workflow will proceed to the next step.
                  The maximum resolution time is 30 days.`}
                htmlFor='step-config-wait__wait-period-seconds'
                labelText='Default resolution time (seconds)'
              />
              <Input
                aria-invalid={hasErrorFor('wait-period-seconds')}
                disabled={!editable || waitableObjects.length === 0}
                hasError={hasErrorFor('wait-period-seconds')}
                id='step-config-wait__wait-period-seconds'
                max={DEFAULT_MAX_WAIT_TIME_SECONDS}
                type='number'
                value={String(step['wait-period-seconds'] ?? '')}
                onChange={onChangeWaitPeriodSeconds}
              />
              {renderErrorMessageFor('wait-period-seconds')}
            </>
          )}
        </>
      )}
      {waitableType === WaitableType.Time && (
        <>
          <Spacer height={2} />
          <ParamLabel
            helpText='This wait step will delay for the specified period of seconds.'
            htmlFor='step-config-wait__delay-period-seconds'
            labelText='Duration to delay (seconds)'
          />
          <Input
            aria-invalid={hasErrorFor('wait-period-seconds')}
            disabled={!editable}
            hasError={hasErrorFor('wait-period-seconds')}
            id='step-config-wait__delay-period-seconds'
            min={MIN_WAIT_TIME_SECONDS}
            max={defaultMaxWaitTimeSeconds}
            type='number'
            value={String(step['wait-period-seconds'] ?? '')}
            onChange={onChangeWaitPeriodSeconds}
          />
          {renderErrorMessageFor('wait-period-seconds')}
        </>
      )}
      <Spacer height={2} />
      <Checkbox
        onChange={(e) => {
          updateStep({
            ...step,
            'error-on-expiration': e.target.checked,
          });
        }}
        checked={!!step['error-on-expiration']}
        aria-label='Error on expiration'
        disabled={!editable}
        label={
          <span>
            Error on expiration
            <StyledTooltip
              iconTrigger={<CircleInfoIcon />}
              hideArrow
              aria-label='Error the workflow run when this step times out'
            >
              <StyledParagraph size='sm'>
                If this option is enabled, the entire workflow run will fail if the wait step
                reaches time out without resolving.
              </StyledParagraph>
            </StyledTooltip>
          </span>
        }
      />
    </>
  );
};

export default StepConfigWait;
